import type { CreateOrganizationUserRequestDto } from '@evimo/types/api/user/create-organization-user/create-organization-user.request.dto';
import type { CreateOrganizationUserResponseDto } from '@evimo/types/api/user/create-organization-user/create-organization-user.response.dto';
import type { ListOrganizationUsersDto } from '@evimo/types/api/user/list-organization-users/list-organization-users.dto';
import type { UpdateUserRequestDto } from '@evimo/types/api/user/update-user/update-user.request.dto';
import type { UpdateUserResponseDto } from '@evimo/types/api/user/update-user/update-user.response.dto';
import type { OrganizationUserControllerListOrganizationUsersRoleEnum } from '@evimo/client/apis';
import { ResponseError } from '@evimo/client/runtime';
import type { OrgUserRole } from '~/app/landing/people/utils';
import {
  userRequestInit,
  usersApi,
} from '~/app/services/user/user-api-instances';

async function readClientError(error: unknown): Promise<never> {
  if (error instanceof ResponseError) {
    const message = await error.response.text();
    throw new Error(message || `Request failed (${error.response.status})`);
  }
  throw error;
}

export const UserService = {
  createOrganizationUser(
    organizationId: string,
    data: CreateOrganizationUserRequestDto,
  ): Promise<CreateOrganizationUserResponseDto> {
    return usersApi
      .organizationUserControllerCreateOrganizationUser(
        {
          organizationId,
          createOrganizationUserRequestDto: data,
        },
        userRequestInit,
      )
      .catch(readClientError);
  },

  listOrganizationUsers(
    organizationId: string,
    role?: OrgUserRole,
    includeSuborganizations?: boolean,
  ): Promise<ListOrganizationUsersDto[]> {
    return usersApi
      .organizationUserControllerListOrganizationUsers(
        {
          organizationId,
          ...(role
            ? {
                role: role as OrganizationUserControllerListOrganizationUsersRoleEnum,
              }
            : {}),
          ...(includeSuborganizations !== undefined
            ? { includeSuborganizations }
            : {}),
        },
        userRequestInit,
      )
      .catch(readClientError);
  },

  async updateUser(
    organizationId: string,
    userId: string,
    data: Partial<UpdateUserRequestDto>,
  ): Promise<UpdateUserResponseDto> {
    try {
      // API returns void; callers historically accepted an empty response object.
      await usersApi.organizationUserControllerUpdate(
        {
          organizationId,
          userId,
          updateUserRequestDto: data as UpdateUserRequestDto,
        },
        userRequestInit,
      );
      return {} as UpdateUserResponseDto;
    } catch (error) {
      return readClientError(error);
    }
  },

  deleteUser(organizationId: string, userId: string): Promise<void> {
    return usersApi
      .organizationUserControllerDelete(
        { organizationId, userId },
        userRequestInit,
      )
      .catch(readClientError);
  },
};
